import { useState, useEffect, useMemo, useRef } from "react";
import ConsoleLayout from "@/components/ConsoleLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { teamsApi } from "@/lib/api";
import { adaptUsageRecord } from "@/lib/adapters";
import type { AdaptedUsageLog, TeamMember, RetryStatsResponse } from "@/lib/types";
import { UsageLogRow } from "@/components/UsageLogRow";
import { Download, ChevronLeft, ChevronRight, Activity, AlertTriangle, Check } from "lucide-react";
import { exportToCsv } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { DatePicker } from "@/components/ui/date-picker";

// Local calendar date (YYYY-MM-DD) for the default "today 00:00 → now" range.
function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Team-wide call-log page. Backed by GET /api/v1/teams/:tid/usage which is
// gated to team_admin (sys_admin always passes). Records are redacted of
// upstream details server-side — we only see the ScienceLLM product key.
// Each row carries a Member column built from the team roster so admins can
// see at a glance who made each call.
export default function TeamUsage() {
  const { t, language } = useLanguage();
  const { teamId, user } = useAuth();
  const isTeamAdmin = user?.role === "team_admin" || user?.is_system_admin;
  
  const [logs, setLogs] = useState<AdaptedUsageLog[]>([]);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [retryStats, setRetryStats] = useState<RetryStatsResponse | null>(null);
  
  const [loading, setLoading] = useState(true);

  // Draft inputs — edited freely, only committed on Query click
  const [model, setModel] = useState("");
  const [requestId, setRequestId] = useState("");
  const [status, setStatus] = useState("");
  const [memberFilter, setMemberFilter] = useState("");
  const [startDate, setStartDate] = useState(todayStr());
  const [endDate, setEndDate] = useState(todayStr());

  // Applied filters — the ONLY values fetchLogs reads
  const [appliedModel, setAppliedModel] = useState("");
  const [appliedRequestId, setAppliedRequestId] = useState("");
  const [appliedStatus, setAppliedStatus] = useState("");
  const [appliedMemberFilter, setAppliedMemberFilter] = useState("");
  const [appliedStartDate, setAppliedStartDate] = useState(todayStr());
  const [appliedEndDate, setAppliedEndDate] = useState(todayStr());

  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [justQueried, setJustQueried] = useState(false);
  const justQueriedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const PAGE_SIZE = 50;

  const buildParams = (limit: number, offset: number) => ({
    limit,
    offset,
    model: appliedModel || undefined,
    request_id: appliedRequestId || undefined,
    status: appliedStatus || undefined,
    user_id: appliedMemberFilter || undefined,
    after: appliedStartDate ? new Date(appliedStartDate + "T00:00:00").toISOString() : undefined,
    before: appliedEndDate ? new Date(appliedEndDate + "T23:59:59").toISOString() : undefined,
  });

  const runQuery = () => {
    if (justQueried) return; // 对号显示期间忽略重复点击/回车
    setAppliedModel(model);
    setAppliedRequestId(requestId);
    setAppliedStatus(status);
    setAppliedMemberFilter(memberFilter);
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setPage(0);
    setReloadNonce((n) => n + 1);
    setJustQueried(true);
    if (justQueriedTimer.current) clearTimeout(justQueriedTimer.current);
    justQueriedTimer.current = setTimeout(() => setJustQueried(false), 1200);
  };

  // Clear the pending 对号 timer on unmount to avoid setState-after-unmount.
  useEffect(() => () => {
    if (justQueriedTimer.current) clearTimeout(justQueriedTimer.current);
  }, []);

  const memberMap = useMemo(() => {
    const m: Record<string, TeamMember> = {};
    members.forEach((u) => { m[u.id] = u; });
    return m;
  }, [members]);

  useEffect(() => {
    if (!teamId) return;
    teamsApi.listMembers(teamId, { includeInactive: true })
      .then((resp) => setMembers(resp.members))
      .catch(() => { /* ignore */ });
  }, [teamId]);

  const fetchLogs = async () => {
    if (!teamId) return;
    setLoading(true);
    try {
      const usage = await teamsApi.getUsage(teamId, buildParams(PAGE_SIZE, page * PAGE_SIZE));
      setLogs(usage.records.map((r) => adaptUsageRecord(r, usage.api_key_names || {}, language)));
      setTotal(usage.total);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, page, reloadNonce]);

  useEffect(() => {
    if (!teamId || !isTeamAdmin) return;

    let fromDate = appliedStartDate;
    let toDate = appliedEndDate;

    // Honor whichever boundary was applied. Only fall back to a 7-day window
    // when BOTH are empty; with today defaults, first load uses today.
    if (!fromDate && !toDate) {
      const now = new Date();
      toDate = now.toISOString().slice(0, 10);
      const past = new Date(now);
      past.setDate(past.getDate() - 7);
      fromDate = past.toISOString().slice(0, 10);
    } else if (!toDate) {
      toDate = new Date().toISOString().slice(0, 10);
    } else if (!fromDate) {
      const end = new Date(toDate);
      end.setDate(end.getDate() - 7);
      fromDate = end.toISOString().slice(0, 10);
    }

    teamsApi.getRetryStats(teamId, {
      from: new Date(fromDate).toISOString(),
      to: new Date(toDate + "T23:59:59").toISOString(),
    })
      .then(setRetryStats)
      .catch(() => setRetryStats(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, reloadNonce]);

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const [exporting, setExporting] = useState(false);

  const renderMemberCell = (log: AdaptedUsageLog) => {
    const u = log.userId ? memberMap[log.userId] : undefined;
    if (!u) {
      return <div className="text-xs text-slate-600 truncate">{t("logs.team.unknownUser")}</div>;
    }
    return (
      <div className="min-w-0">
        <div className="text-xs text-slate-300 truncate">{u.display_name || u.email}</div>
        <div className="text-[10px] text-slate-600 truncate">{u.email}</div>
      </div>
    );
  };

  const handleExportCsv = async () => {
    if (total === 0 || !teamId) return;
    setExporting(true);
    try {
      const allUsage = await teamsApi.getUsage(teamId, buildParams(total, 0));
      const allLogs = allUsage.records.map((r) => adaptUsageRecord(r, allUsage.api_key_names || {}, language));
      exportToCsv(
        `team-call-logs-${new Date().toISOString().slice(0, 10)}.csv`,
        ["Request ID", "Model", "Type", "Status", "Timestamp", "Input Tokens", "Output Tokens", "Cache Read Tokens", "Cache Write Tokens", "Total Tokens", "Latency (ms)", "Cost (USD)", "API Key", "User", "Email", "Error"],
        allLogs.map((l) => {
          const u = l.userId ? memberMap[l.userId] : undefined;
          return [l.requestId, l.model, l.type, l.status, l.timestamp, String(l.inputTokens || 0), String(l.outputTokens || 0), String(l.cacheReadTokens || 0), String(l.cacheWriteTokens || 0), String(l.tokens), String(l.latency), l.cost, l.keyName, u?.display_name || "", u?.email || l.userId || "", l.errorMessage || ""];
        })
      );
    } catch {
      // ignore
    } finally {
      setExporting(false);
    }
  };

  return (
    <ConsoleLayout
      title={t("logs.team.title")}
      subtitle={t("logs.team.subtitle")}
      actions={
        <Button size="sm" variant="ghost" className="gap-1.5 text-xs h-8 text-slate-400 border border-white/10" onClick={handleExportCsv} disabled={total === 0 || exporting}>
          <Download size={12} />
          {exporting ? "..." : t("logs.exportCsv")}
        </Button>
      }
    >
      <div className="p-6 space-y-5">
        {/* Retry Stats Cards */}
        {isTeamAdmin && retryStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="sci-card p-4 flex flex-col gap-1">
              <div className="text-xs text-slate-500 flex items-center gap-1.5">
                <Activity size={12} className="text-blue-400" />
                {t("logs.team.avgAttempts") || "平均尝试次数"}
              </div>
              <div className="text-2xl font-mono text-white">
                {retryStats.avg_attempts_per_request.toFixed(1)}
              </div>
            </div>
            <div className="sci-card p-4 flex flex-col gap-1">
              <div className="text-xs text-slate-500 flex items-center gap-1.5">
                <AlertTriangle size={12} className="text-amber-400" />
                {t("logs.team.retryRate") || "重试率"}
              </div>
              <div className="text-2xl font-mono text-white">
                {(retryStats.retry_rate * 100).toFixed(1)}<span className="text-sm text-slate-500 ml-1">%</span>
              </div>
            </div>
            <div className="sci-card p-4 flex flex-col gap-1">
              <div className="text-xs text-slate-500">
                {t("logs.team.terminalCount") || "Terminal Count"}
              </div>
              <div className="text-xl font-mono text-red-400">
                {retryStats.terminal_count}
              </div>
            </div>
            <div className="sci-card p-4 flex flex-col gap-1">
              <div className="text-xs text-slate-500">
                {t("logs.team.budgetExceededCount") || "Budget Exceeded"}
              </div>
              <div className="text-xl font-mono text-orange-400">
                {retryStats.budget_exceeded_count}
              </div>
            </div>
          </div>
        )}

        {/* Filters: Time — Model — RequestId — Status — Member — Query */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <DatePicker value={startDate} onChange={setStartDate} />
            <span className="text-xs text-slate-600">—</span>
            <DatePicker value={endDate} onChange={setEndDate} />
          </div>
          <Input
            placeholder={t("logs.searchModelPlaceholder")}
            value={model}
            onChange={(e) => setModel(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") runQuery(); }}
            className="flex-1 min-w-[160px] max-w-[220px] bg-white/5 border-white/10 text-sm h-9 text-white placeholder:text-slate-600"
          />
          <Input
            placeholder={t("logs.searchRequestIdPlaceholder")}
            value={requestId}
            onChange={(e) => setRequestId(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") runQuery(); }}
            maxLength={26}
            className="flex-1 min-w-[160px] max-w-[240px] bg-white/5 border-white/10 text-sm h-9 text-white placeholder:text-slate-600 font-mono"
          />
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white h-9 focus:outline-none focus:border-blue-500/50"
          >
            <option value="">{t("logs.statusAll")}</option>
            <option value="success">{t("logs.statusSuccess")}</option>
            <option value="failed">{t("logs.statusFailed")}</option>
            <option value="running">{t("logs.statusRunning")}</option>
          </select>
          <select
            value={memberFilter}
            onChange={(e) => setMemberFilter(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white h-9 focus:outline-none focus:border-blue-500/50 max-w-[200px]"
          >
            <option value="">{t("logs.team.allMembers")}</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.display_name || m.email}
              </option>
            ))}
          </select>
          <Button size="sm" variant="ghost" onClick={runQuery} className="relative h-9 px-4 text-xs text-slate-300 border border-white/10 active:bg-white/10">
            <span className={justQueried ? "invisible" : undefined}>{t("logs.applyFilters")}</span>
            {justQueried && (
              <span className="absolute inset-0 flex items-center justify-center text-emerald-400">
                <Check size={14} />
              </span>
            )}
          </Button>
          <div className="ml-auto flex items-center gap-3">
            <span className="text-xs text-slate-500">{total} {t("logs.records")}</span>
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  className="p-1 rounded text-slate-500 hover:text-white disabled:opacity-30"
                >
                  <ChevronLeft size={14} />
                </button>
                <span className="text-xs text-slate-400">{page + 1} / {totalPages}</span>
                <button
                  onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                  disabled={page >= totalPages - 1}
                  className="p-1 rounded text-slate-500 hover:text-white disabled:opacity-30"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Logs Table */}
        <div className="sci-card overflow-hidden">
          <div className="px-5 py-3 border-b border-white/8 grid grid-cols-[24px_1fr_160px_120px_80px_20px] gap-4 text-[10px] text-slate-600 uppercase tracking-wider items-center">
            <div />
            <div>{t("logs.modelRequest")}</div>
            <div className="hidden lg:block">{t("logs.team.member")}</div>
            <div className="hidden sm:block text-right">{t("logs.usage")}</div>
            <div className="text-right">{t("logs.cost")}</div>
            <div />
          </div>
          {logs.length > 0 ? (
            logs.map((log) => (
              <UsageLogRow
                key={log.id}
                log={log}
                extraColumn={renderMemberCell(log)}
                extraDetailFields={
                  log.userId
                    ? [["User", memberMap[log.userId]?.email || log.userId]]
                    : []
                }
              />
            ))
          ) : (
            <div className="py-16 text-center text-slate-500 text-sm">
              {loading ? "Loading..." : t("logs.noLogs")}
            </div>
          )}
        </div>
      </div>
    </ConsoleLayout>
  );
}
